import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";

import { templates } from "@reactivated";
import type {
    CharBlock as CharBlockValue,
    ImageChooserBlock as ImageChooserBlockValue,
    RawHTMLBlock as RawHTMLBlockValue,
    RichTextBlock as RichTextBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import {
    ToolboxPageSSR,
    buildToolboxPageMap,
} from "../../blocks/toolboxPageMap";

/**
 * tempurpedic BasicPage body assembly (#33588).
 *
 * ``BasicPageContent`` = the ``Toolbox`` plus four PRIMITIVE content keys
 * (``heading`` CharBlock, ``paragraph`` RichTextBlock, ``image``
 * ImageChooserBlock, ``html`` RawHTMLBlock) that have NO inline ``Meta.template``
 * -- so they are reproduced here inline as Wagtail's default ``render_basic``
 * (toppers/pillows UtilityPage primitive-keys precedent):
 *   - ``heading`` (CharBlock) -> escaped text (``force_str``, no wrapper);
 *   - ``paragraph`` (RichTextBlock) -> the serialized value is ALREADY the
 *     ``<div class="rich-text">`` -wrapped HTML (thelabui ``RichTextBlock`` =
 *     ClientRenderingBarrier -> ``render`` -> ``render_basic`` ->
 *     ``richtext.html``, and this project installs
 *     ``wagtail.contrib.legacy.richtext`` which adds the wrapper), so it is
 *     raw-injected; a thelabui ``<RichTextBlock className="rich-text">`` would
 *     DOUBLE-wrap;
 *   - ``image`` (ImageChooserBlock) -> eager ``<img>`` from the serialized
 *     ``value.url`` (the original-file URL; the eager-image convention --
 *     legacy ``image-chooser-block.html`` lazysizes/responsive plumbing is not
 *     reproduced, matching the shipped libs blocks);
 *   - ``html`` (RawHTMLBlock) -> the shared ``RawHTMLBlock`` component, which
 *     strips the ``<div class="raw-html-block">`` wrapper ``RawHTML.__new__``
 *     pre-applies and re-applies it as the single rendered div's class
 *     (``.block-html > .raw-html-block``, the same DOM the legacy
 *     ``{% include_block %}`` of a RawHTMLBlock produced -- NOT a bare
 *     ``dangerouslySetInnerHTML``, which would nest an extra wrapper div). Same
 *     entry as the PDP ``HTML`` key.
 * ``accordion_section`` is already a Toolbox key. (These primitives live only on
 * the top-level page StreamBlock, never nested in ``ToolboxContent``, so they
 * are spread on top of the shared map -- they never need to reach the recursive
 * container entries.)
 *
 * WRAPPER PARITY (load-bearing): the legacy ``basic_page.html`` rendered
 * ``{{ self.body }}`` -- the whole StreamField -- so Wagtail's
 * ``StreamBlock.render_basic`` wrapped every top-level block in
 * ``<div class="w-block-{type} block-{type}">``. Reproduced per top-level block
 * via the per-block ``StreamField tagName="div"`` render (sealy BasicPage
 * precedent).
 *
 * The four page/request/settings-dependent toolbox blocks (and nested ones) are
 * handled by ``buildToolboxPageMap`` via the ``get_toolbox_block_ssr``
 * side-channel.
 */
type IProps = Immutable<
    Pick<
        templates.BasicPageTemplate,
        | "page"
        | "hardcoded_ssr"
        | "hotspot_ssr"
        | "breadcrumbs_ssr"
        | "customer_service_ssr"
        | "shop_compare_ssr"
        | "tab_feature_attribute_ssr"
        | "reviews_ssr"
    >
>;

/**
 * The page-derived SSR side-channels the BasicPage `body` component map closes
 * over (only what `buildToolboxPageMap` needs -- the primitive extras below are
 * page-independent). Split out from `IProps` so the styleguide block-render
 * registry can build the same map with minimal (zeroed) props.
 */
type IMapProps = Immutable<ToolboxPageSSR>;

const buildBasicPageBodyComponentsMap = (props: IMapProps) => {
    const map = {
        ...buildToolboxPageMap(props),
        // --- BasicPageContent primitive extras (Wagtail default render_basic) ---
        heading: (block: { value: Immutable<CharBlockValue> }) => (
            <>{block.value}</>
        ),
        paragraph: (block: { value: Immutable<RichTextBlockValue> }) => (
            <div dangerouslySetInnerHTML={{ __html: block.value }} />
        ),
        image: (block: { value: Immutable<ImageChooserBlockValue> }) => (
            <img
                src={block.value.url}
                alt={block.value.default_alt_text}
                width={block.value.width}
                height={block.value.height}
                loading="lazy"
                decoding="async"
            />
        ),
        html: (block: { value: Immutable<RawHTMLBlockValue> }) => (
            <RawHTMLBlock value={block.value} />
        ),
    };
    return map;
};

export const BasicPageBody = (props: IProps): React.JSX.Element | null => {
    // `body` is nullable (`null=True` on the model field).
    if (!props.page.body) {
        return null;
    }
    const map = buildBasicPageBodyComponentsMap(props);
    return (
        <>
            {props.page.body.value.map((block) => (
                <StreamField
                    key={block.id}
                    tagName="div"
                    attrs={{
                        className: `w-block-${block.type} block-${block.type}`,
                    }}
                    value={[block]}
                    components={map}
                />
            ))}
        </>
    );
};
